import React, { useState } from 'react';
import { alpha, Box, ButtonBase, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BoltIcon from '@mui/icons-material/Bolt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { count } from '../manage/manage';
import { StepIcon } from './StepIcon';
import {
  describeOffset,
  describeStep,
  describeTrigger,
  newKey,
  newStep,
  STEP_CHOICES,
  STEP_META,
  StepDraft,
  stepOffsets,
  StepType,
  WorkflowSettings,
} from './workflowModel';

/**
 * The workflow as a flow: the trigger, then each step joined by a line, top to
 * bottom, the way it runs. A straight line is all the API runs (plan 4.5 MVP),
 * so this draws one; waits sit on the line itself as pills, because they are
 * the time between steps rather than something done.
 *
 * Editable: a "+" on every line inserts a step there, a step can be dragged
 * onto another line (or moved with its arrows), copied or removed, and a click
 * opens it in the inspector beside the canvas. Read-only on a workflow that has
 * people in it: each step then shows how many have passed it and how many wait
 * there, and a simulation's reach when there is one.
 */

export const TRIGGER_KEY = 'trigger';

export interface CanvasReach {
  /** Who the simulation would enrol. */
  wouldEnroll: number;
  /** Per step position, how many the consent gate would let a message reach. */
  byPosition: Map<number, number>;
}

interface WorkflowCanvasProps {
  settings: WorkflowSettings;
  steps: ReadonlyArray<StepDraft>;
  editable: boolean;
  selected: string | null;
  onSelect: (key: string | null) => void;
  onChange?: (steps: Array<StepDraft>) => void;
  /** By step index: what the API would refuse. */
  problems?: Map<number, string>;
  /** Everyone ever enrolled, for each step's funnel. Omitted on a new workflow. */
  enrolled?: number;
  reach?: CanvasReach | null;
}

const NODE_WIDTH = 400;

export const WorkflowCanvas = ({ settings, steps, editable, selected, onSelect, onChange, problems, enrolled, reach }: WorkflowCanvasProps) => {
  const [menu, setMenu] = useState<{ anchor: HTMLElement; at: number } | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const offsets = stepOffsets(steps);

  const change = (next: Array<StepDraft>) => onChange?.(next);

  const insert = (at: number, type: StepType) => {
    const step = newStep(type);
    change([...steps.slice(0, at), step, ...steps.slice(at)]);
    setMenu(null);
    onSelect(step.key);
  };

  const move = (from: number, to: number) => {
    // `to` is a gap index (0..length): where the step lands, counted before it is lifted out.
    if (to === from || to === from + 1) return;
    const next = [...steps];
    const [step] = next.splice(from, 1);
    next.splice(to > from ? to - 1 : to, 0, step);
    change(next);
  };

  const remove = (index: number) => {
    const key = steps[index].key;
    change(steps.filter((_, i) => i !== index));
    if (selected === key) onSelect(null);
  };

  const duplicate = (index: number) => {
    const copy = { ...steps[index], key: newKey(), waiting: undefined, executed: undefined };
    change([...steps.slice(0, index + 1), copy, ...steps.slice(index + 1)]);
    onSelect(copy.key);
  };

  const endsWithEnd = steps.length > 0 && steps[steps.length - 1].type === 'END';

  const gap = (at: number, last = false) => (
    <Connector
      key={`gap-${at}`}
      editable={editable}
      dropping={dragging !== null}
      over={over === at}
      tall={last && !endsWithEnd}
      onAdd={(anchor) => setMenu({ anchor, at })}
      onDragOver={() => setOver(at)}
      onDragLeave={() => setOver((o) => (o === at ? null : o))}
      onDrop={() => {
        const from = steps.findIndex((s) => s.key === dragging);
        if (from >= 0) move(from, at);
        setDragging(null);
        setOver(null);
      }}
      label={at === steps.length ? 'Add a step at the end' : `Add a step before step ${at + 1}`}
    />
  );

  return (
    <Box
      role="region"
      aria-label="Workflow steps"
      onClick={(event) => {
        if (event.target === event.currentTarget) onSelect(null);
      }}
      sx={{
        position: 'relative',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#f8fafc',
        backgroundImage: 'radial-gradient(#d9dee7 1px, transparent 1px)',
        backgroundSize: '18px 18px',
        px: { xs: 1.5, sm: 4 },
        py: { xs: 3, sm: 5 },
        minHeight: 420,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '@keyframes wfIn': {
          from: { opacity: 0, transform: 'translateY(6px) scale(0.98)' },
          to: { opacity: 1, transform: 'none' },
        },
      }}
    >
      <TriggerNode settings={settings} selected={selected === TRIGGER_KEY} onSelect={() => onSelect(TRIGGER_KEY)} />

      {steps.map((step, index) => (
        <React.Fragment key={step.key}>
          {gap(index)}
          {step.type === 'DELAY' ? (
            <DelayPill
              step={step}
              index={index}
              selected={selected === step.key}
              problem={problems?.get(index)}
              editable={editable}
              dragging={dragging === step.key}
              waiting={enrolled !== undefined ? step.waiting : undefined}
              onSelect={() => onSelect(step.key)}
              onDragStart={() => setDragging(step.key)}
              onDragEnd={() => {
                setDragging(null);
                setOver(null);
              }}
              onRemove={() => remove(index)}
            />
          ) : (
            <StepNode
              step={step}
              index={index}
              last={index === steps.length - 1}
              offset={offsets[index]}
              selected={selected === step.key}
              problem={problems?.get(index)}
              editable={editable}
              dragging={dragging === step.key}
              enrolled={enrolled}
              reach={reach ? { of: reach.wouldEnroll, n: reach.byPosition.get(index) } : undefined}
              onSelect={() => onSelect(step.key)}
              onDragStart={() => setDragging(step.key)}
              onDragEnd={() => {
                setDragging(null);
                setOver(null);
              }}
              onMoveUp={index > 0 ? () => move(index, index - 1) : undefined}
              onMoveDown={index < steps.length - 1 ? () => move(index, index + 2) : undefined}
              onDuplicate={step.type === 'END' ? undefined : () => duplicate(index)}
              onRemove={() => remove(index)}
            />
          )}
        </React.Fragment>
      ))}

      {editable && !endsWithEnd && gap(steps.length, true)}
      {!editable && steps.length > 0 && <Line height={24} />}
      {!endsWithEnd && <EndCap empty={steps.length === 0} editable={editable} />}

      {steps.length === 0 && editable && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center', maxWidth: 360 }}>
          Press <b>+</b> to add the first step: an email, a text, a tag, a call task, or a wait.
        </Typography>
      )}

      <AddStepMenu
        anchor={menu?.anchor ?? null}
        atEnd={menu ? menu.at === steps.length : false}
        onClose={() => setMenu(null)}
        onPick={(type) => menu && insert(menu.at, type)}
      />
    </Box>
  );
};

/* ------------------------------------------------------------------ *
 * The line between nodes, with its "+" and its drop zone
 * ------------------------------------------------------------------ */

const LINE = '#c7cfdb';

const Line = ({ height }: { height: number }) => <Box aria-hidden sx={{ width: 2, height, bgcolor: LINE, borderRadius: 1 }} />;

interface ConnectorProps {
  editable: boolean;
  dropping: boolean;
  over: boolean;
  tall: boolean;
  label: string;
  onAdd: (anchor: HTMLElement) => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
}

const Connector = ({ editable, dropping, over, tall, label, onAdd, onDragOver, onDragLeave, onDrop }: ConnectorProps) => {
  if (!editable) return <Line height={28} />;

  return (
    <Box
      onDragOver={(event) => {
        event.preventDefault();
        onDragOver();
      }}
      onDragLeave={onDragLeave}
      onDrop={(event) => {
        event.preventDefault();
        onDrop();
      }}
      sx={{
        position: 'relative',
        height: tall ? 64 : 52,
        width: NODE_WIDTH,
        maxWidth: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 'calc(50% - 1px)',
          width: 2,
          bgcolor: LINE,
          borderRadius: 1,
        },
      }}
    >
      {dropping ? (
        <Box
          aria-hidden
          sx={{
            position: 'relative',
            width: '100%',
            height: 12,
            borderRadius: 6,
            border: '2px dashed',
            borderColor: over ? 'primary.main' : alpha('#4f46e5', 0.25),
            bgcolor: over ? alpha('#4f46e5', 0.1) : 'transparent',
            transition: 'all 120ms',
          }}
        />
      ) : (
        <Tooltip title={label} placement="right" describeChild>
          <IconButton
            size="small"
            aria-label={label}
            onClick={(event) => onAdd(event.currentTarget)}
            sx={{
              position: 'relative',
              width: 26,
              height: 26,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: LINE,
              color: 'text.secondary',
              boxShadow: '0 1px 2px rgba(15,23,42,.06)',
              transition: 'all 120ms',
              '&:hover': { bgcolor: 'primary.main', borderColor: 'primary.main', color: '#fff', transform: 'scale(1.12)' },
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------ *
 * Nodes
 * ------------------------------------------------------------------ */

const nodeSx = (tone: string, selected: boolean, problem: boolean, dragging: boolean) => ({
  position: 'relative' as const,
  width: NODE_WIDTH,
  maxWidth: '100%',
  textAlign: 'left' as const,
  borderRadius: '14px',
  border: '1px solid',
  borderColor: problem ? '#ef4444' : selected ? tone : '#e2e8f0',
  bgcolor: 'background.paper',
  boxShadow: selected
    ? `0 0 0 3px ${alpha(tone, 0.18)}, 0 8px 24px rgba(15,23,42,.08)`
    : '0 1px 2px rgba(15,23,42,.05), 0 2px 8px rgba(15,23,42,.04)',
  opacity: dragging ? 0.45 : 1,
  transition: 'border-color 120ms, box-shadow 160ms, transform 160ms, opacity 120ms',
  animation: 'wfIn 220ms ease-out both',
  '&:hover': { borderColor: problem ? '#ef4444' : alpha(tone, 0.6), transform: 'translateY(-1px)' },
  '&:hover .wf-tools, &:focus-within .wf-tools': { opacity: 1 },
  '@media (hover: none)': { '& .wf-tools': { opacity: 1 } },
});

const TriggerNode = ({ settings, selected, onSelect }: { settings: WorkflowSettings; selected: boolean; onSelect: () => void }) => {
  const tone = '#4f46e5';
  const detail =
    settings.triggerType === 'MANUAL'
      ? 'People are added from this page or the contact list'
      : settings.includeExisting
        ? 'Everyone already there is enrolled when it first starts'
        : 'Only people who join after it starts';

  return (
    <ButtonBase onClick={onSelect} aria-pressed={selected} aria-label={`Trigger: ${describeTrigger(settings)}`} sx={{ ...nodeSx(tone, selected, false, false), display: 'block', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 0.75, background: `linear-gradient(90deg, ${tone}, #7c3aed)`, color: '#fff', display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <BoltIcon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Trigger</Typography>
      </Box>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography sx={{ fontWeight: 650, fontSize: '0.95rem' }}>{describeTrigger(settings)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      </Box>
    </ButtonBase>
  );
};

interface StepNodeProps {
  step: StepDraft;
  index: number;
  last: boolean;
  offset: number | null;
  selected: boolean;
  problem?: string;
  editable: boolean;
  dragging: boolean;
  enrolled?: number;
  reach?: { of: number; n: number | undefined };
  onSelect: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDuplicate?: () => void;
  onRemove: () => void;
}

const StepNode = ({
  step,
  index,
  offset,
  selected,
  problem,
  editable,
  dragging,
  enrolled,
  reach,
  onSelect,
  onDragStart,
  onDragEnd,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onRemove,
}: StepNodeProps) => {
  const meta = STEP_META[step.type];
  const isEnd = step.type === 'END';

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`Step ${index + 1}: ${meta.label}. ${describeStep(step)}`}
      draggable={editable}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', step.key);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={{ ...nodeSx(meta.tone, selected, !!problem, dragging), cursor: editable ? 'grab' : 'pointer', outline: 'none', '&:focus-visible': { boxShadow: `0 0 0 3px ${alpha(meta.tone, 0.35)}` } }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.5 }}>
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            borderRadius: '11px',
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            color: meta.tone,
            bgcolor: alpha(meta.tone, 0.1),
            border: `1px solid ${alpha(meta.tone, 0.18)}`,
          }}
        >
          <StepIcon type={step.type} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 650, fontSize: '0.9rem' }}>{meta.label}</Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
              Step {index + 1}
            </Typography>
            {!isEnd && (
              <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                <ScheduleIcon sx={{ fontSize: 12 }} />
                {describeOffset(offset)}
              </Typography>
            )}
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}
          >
            {describeStep(step)}
          </Typography>
          {problem && (
            <Typography variant="caption" sx={{ color: '#dc2626', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <ErrorOutlineIcon sx={{ fontSize: 14 }} />
              {problem}
            </Typography>
          )}
          {reach && reach.n !== undefined && meta.sends && <ReachBar reach={reach.n} of={reach.of} tone={meta.tone} />}
        </Box>
      </Box>

      {enrolled !== undefined && step.executed !== undefined && <Funnel executed={step.executed} waiting={step.waiting ?? 0} enrolled={enrolled} tone={meta.tone} />}

      {editable && (
        <Box
          className="wf-tools"
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: 'absolute',
            top: -15,
            right: 10,
            display: 'flex',
            gap: 0.25,
            px: 0.5,
            py: 0.25,
            borderRadius: 99,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: '#e2e8f0',
            boxShadow: '0 2px 8px rgba(15,23,42,.10)',
            opacity: selected ? 1 : 0,
            transition: 'opacity 120ms',
          }}
        >
          <Tool label="Drag to move" icon={<DragIndicatorIcon />} onClick={undefined} />
          {onMoveUp && <Tool label={`Move step ${index + 1} up`} icon={<ArrowUpwardIcon />} onClick={onMoveUp} />}
          {onMoveDown && <Tool label={`Move step ${index + 1} down`} icon={<ArrowDownwardIcon />} onClick={onMoveDown} />}
          {onDuplicate && <Tool label={`Copy step ${index + 1}`} icon={<ContentCopyIcon />} onClick={onDuplicate} />}
          <Tool label={`Remove step ${index + 1}`} icon={<DeleteOutlineIcon />} onClick={onRemove} danger />
        </Box>
      )}
    </Box>
  );
};

const Tool = ({ label, icon, onClick, danger }: { label: string; icon: React.ReactElement; onClick?: () => void; danger?: boolean }) => (
  <Tooltip title={label} placement="right" describeChild>
    <Box component="span">
      <IconButton
        size="small"
        aria-label={label}
        onClick={onClick}
        disabled={!onClick}
        sx={{
          width: 24,
          height: 24,
          color: 'text.secondary',
          '&.Mui-disabled': { color: 'text.disabled', cursor: 'grab' },
          '&:hover': { color: danger ? 'error.main' : 'primary.main', bgcolor: danger ? alpha('#ef4444', 0.08) : alpha('#4f46e5', 0.08) },
          '& svg': { fontSize: 15 },
        }}
      >
        {icon}
      </IconButton>
    </Box>
  </Tooltip>
);

/** How many have passed a step and how many wait at it, against everyone enrolled. */
const Funnel = ({ executed, waiting, enrolled, tone }: { executed: number; waiting: number; enrolled: number; tone: string }) => {
  const share = (n: number) => (enrolled > 0 ? Math.min(100, (n / enrolled) * 100) : 0);

  return (
    <Box sx={{ px: 1.5, pb: 1.25 }}>
      <Box aria-hidden sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', bgcolor: '#eef1f5' }}>
        <Box sx={{ width: `${share(executed)}%`, bgcolor: tone, transition: 'width 400ms' }} />
        <Box sx={{ width: `${share(waiting)}%`, bgcolor: alpha(tone, 0.35), transition: 'width 400ms' }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          <b>{count(executed)}</b> done
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {count(waiting)} waiting here
        </Typography>
      </Box>
    </Box>
  );
};

/** What a message step could reach of everyone the simulation would enrol. */
const ReachBar = ({ reach, of, tone }: { reach: number; of: number; tone: string }) => {
  const pct = of > 0 ? Math.round((reach / of) * 100) : 0;
  return (
    <Box sx={{ mt: 1, p: 1, borderRadius: 2, bgcolor: alpha(tone, 0.06), border: `1px dashed ${alpha(tone, 0.35)}` }}>
      <Typography variant="caption" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
        Reaches <b>{count(reach)}</b> of {count(of)} ({pct}%)
      </Typography>
      <Typography variant="caption" color="text.secondary">
        The rest are not opted in, have no address or number, or are do-not-contact.
      </Typography>
    </Box>
  );
};

interface DelayPillProps {
  step: StepDraft;
  index: number;
  selected: boolean;
  problem?: string;
  editable: boolean;
  dragging: boolean;
  waiting?: number;
  onSelect: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onRemove: () => void;
}

/** A wait, drawn on the line: it is time passing between steps. */
const DelayPill = ({ step, index, selected, problem, editable, dragging, waiting, onSelect, onDragStart, onDragEnd, onRemove }: DelayPillProps) => {
  const tone = STEP_META.DELAY.tone;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        aria-label={`Step ${index + 1}: ${describeStep(step)}`}
        draggable={editable}
        onDragStart={(event) => {
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData('text/plain', step.key);
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        onClick={onSelect}
        onKeyDown={(event) => {
          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onSelect();
          }
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          pl: 1.25,
          pr: editable ? 0.5 : 1.5,
          py: 0.5,
          borderRadius: 99,
          border: '1px solid',
          borderColor: problem ? '#ef4444' : selected ? tone : alpha(tone, 0.4),
          bgcolor: selected ? alpha(tone, 0.14) : '#fffbeb',
          color: '#92400e',
          fontSize: '0.8125rem',
          fontWeight: 600,
          cursor: editable ? 'grab' : 'pointer',
          opacity: dragging ? 0.45 : 1,
          boxShadow: selected ? `0 0 0 3px ${alpha(tone, 0.2)}` : '0 1px 2px rgba(15,23,42,.05)',
          animation: 'wfIn 220ms ease-out both',
          transition: 'all 120ms',
          outline: 'none',
          '&:hover': { borderColor: tone },
          '&:focus-visible': { boxShadow: `0 0 0 3px ${alpha(tone, 0.35)}` },
        }}
      >
        <ScheduleIcon sx={{ fontSize: 16, color: tone }} />
        {describeStep(step)}
        {waiting !== undefined && waiting > 0 && (
          <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            · {count(waiting)} waiting
          </Box>
        )}
        {editable && (
          <IconButton
            size="small"
            aria-label={`Remove step ${index + 1}`}
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
            sx={{ width: 22, height: 22, color: alpha('#92400e', 0.6), '&:hover': { color: 'error.main' } }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      {problem && (
        <Typography variant="caption" sx={{ color: '#dc2626', mt: 0.5 }}>
          {problem}
        </Typography>
      )}
    </Box>
  );
};

const EndCap = ({ empty, editable }: { empty: boolean; editable: boolean }) => (
  <Box
    aria-hidden={!empty || !editable ? undefined : true}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.5,
      borderRadius: 99,
      bgcolor: '#eef1f5',
      color: 'text.secondary',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    }}
  >
    <StepIcon type="END" fontSize={14} />
    {empty ? 'No steps yet' : 'Finished'}
  </Box>
);

/* ------------------------------------------------------------------ *
 * Adding a step
 * ------------------------------------------------------------------ */

const AddStepMenu = ({
  anchor,
  atEnd,
  onClose,
  onPick,
}: {
  anchor: HTMLElement | null;
  atEnd: boolean;
  onClose: () => void;
  onPick: (type: StepType) => void;
}) => (
  <Popover
    open={!!anchor}
    anchorEl={anchor}
    onClose={onClose}
    anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
    transformOrigin={{ vertical: 'center', horizontal: 'left' }}
    PaperProps={{ sx: { ml: 1.5, borderRadius: 3, boxShadow: '0 12px 40px rgba(15,23,42,.18)' } }}
  >
    <Box sx={{ p: 1.5, width: { xs: 300, sm: 460 } }}>
      <Typography variant="overline" color="text.secondary" sx={{ px: 0.5 }}>
        Add a step
      </Typography>
      <Box role="menu" aria-label="Step types" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5 }}>
        {STEP_CHOICES.filter((type) => type !== 'END' || atEnd).map((type) => {
          const meta = STEP_META[type];
          return (
            <ButtonBase
              key={type}
              role="menuitem"
              onClick={() => onPick(type)}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                gap: 1.25,
                p: 1,
                borderRadius: 2,
                transition: 'background-color 100ms',
                '&:hover, &.Mui-focusVisible': { bgcolor: alpha(meta.tone, 0.08) },
              }}
            >
              <Box sx={{ width: 34, height: 34, borderRadius: '10px', display: 'grid', placeItems: 'center', color: meta.tone, bgcolor: alpha(meta.tone, 0.12), flexShrink: 0 }}>
                <StepIcon type={type} fontSize={18} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{meta.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, display: 'block' }}>
                  {meta.hint}
                </Typography>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  </Popover>
);
